import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  Box, Button, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, TextField, FormControlLabel, Checkbox, FormGroup, Table, TableBody, TableCell, TableContainer, TableRow,Switch
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RatingService from '../RatingService';
import ConsumptionInputs from '../ConsumptionInputs';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import CircularProgress from '@mui/material/CircularProgress';
import { formatNumber } from '../Utils/Converters';


const SubMeterManager = ({ indexTypes, subMeters, setSubMeters, tariffId, buildRequest, setResult, result }) => {
  const [open, setOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [idCounter, setIdCounter] = useState(1);

  const containerRef = useRef();
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);

  useEffect(() => {
    if (!tariffId) return;
    const req = buildRequest();
    RatingService.rating(req).then(res => {
      const updatedResult = {
        ...res.data,
        subMeters: res.data.subMeters.map((m) => {
          const local = subMeters.find(s => s.subMeterId === m.subMeterId);
          return {
            ...m,
            name: local?.name || null,
            lowPriority: local?.lowPriority || false
          };
        })
      };
      setResult(updatedResult);
    });
  }, [subMeters]);

  const handleAddClick = () => {
    setFormValues({});
    setEditingIndex(null);
    setOpen(true);
  };

  const handleEditClick = (subMeterId) => {
    const foundIndex = subMeters.findIndex(m => m.subMeterId === subMeterId);
    if (foundIndex === -1) return;
    setFormValues(subMeters[foundIndex]);
    setEditingIndex(foundIndex);
    setOpen(true);
  };

  const handleDeleteClick = (subMeterIdToDelete) => {
    const updated = subMeters.filter(m => m.subMeterId !== subMeterIdToDelete);
    setSubMeters(updated);
  };

  const handleDialogClose = () => {
    setOpen(false);
    setFormValues({});
    setEditingIndex(null);
  };

  const handleSave = () => {
    const newItem = {
      ...formValues,
      subMeterId: editingIndex !== null ? formValues.subMeterId : idCounter,
      priority: formValues.lowPriority ? 2 : 1
    };

    if (editingIndex !== null) {
      const updated = [...subMeters];
      updated[editingIndex] = newItem;
      setSubMeters(updated);
    } else {
      setSubMeters([...subMeters, newItem]);
      setIdCounter(idCounter + 1);
    }

    handleDialogClose();
  };

  const handleDetailOpen = (meter) => {
    setSelectedMeter(meter);
    setDetailDialogOpen(true);
  };

  const handleDetailClose = () => {
    setDetailDialogOpen(false);
    setSelectedMeter(null);
  };

  const getMeterLabel = (meter) => {
    if (!meter) return '';
    if (meter.subMeterId === -1) return 'Ana Sayaç';
    if (meter.name?.trim()) return meter.name;
    return `Sayaç ${meter.subMeterId}`;
  };

  return (
    <Box ref={containerRef} mt={4} display="flex" flexDirection="column" alignItems="flex-start" ml={4}>
      {result && (
        <Paper sx={{ p: 2, minWidth: 200, textAlign: 'center', bgcolor: '#f0f0f0', mb: 2 }}>
          <Typography variant="subtitle2">Fatura</Typography>
          <Typography variant="h6">

            {formatNumber(result.totalCharge)} TL
          </Typography>
        </Paper>
      )}

      {[...result?.subMeters || []]
        .sort((a, b) => {
          if (a.subMeterId === -1) return -1;
          if (b.subMeterId === -1) return 1;

          // Ana sayaç değilse, lowPriority: false (priority 1) önce gelsin
          if (a.lowPriority === b.lowPriority) return 0;
          return a.lowPriority ? 1 : -1;
        })
        .map((meter) => {
          const label =
            meter.subMeterId === -1
              ? 'Ana Sayaç'
              : meter.name?.trim()
                ? meter.name
                : `Sayaç ${meter.subMeterId}`;

          const percent = result.totalCharge > 0 ? ((meter.totalCharge / result.totalCharge) * 100).toFixed(2) : '0.00';

          return (
            <Paper
              key={meter.subMeterId}
              onClick={() => handleDetailOpen(meter)}
              sx={{
                p: 2,
                minWidth: 200,
                textAlign: 'center',
                position: 'relative',
                bgcolor: meter.subMeterId === -1 ? '#e0f7fa' : '#fff',
                mb: 2,
                ml: meter.subMeterId === -1 ? 2 : 2,
                cursor: 'pointer'
              }}
            >
              {meter.subMeterId !== -1 && (
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleEditClick(meter.subMeterId); }}
                    sx={{ position: 'absolute', top: 4, right: 36 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(meter.subMeterId); }}
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>                </>
              )}
              <Typography variant="subtitle2">{label}</Typography>
              <Typography variant="body1" fontWeight="bold">{formatNumber(meter.totalCharge)} TL</Typography>
              <>
                <Box sx={{ position: 'absolute', bottom: 4, left: 4 }}>
                  {meter.lowPriority ? (
                    <LooksTwoIcon fontSize="small" color="action" />
                  ) : (
                    <LooksOneIcon fontSize="small" color="primary" />
                  )}
                </Box>
                <Box sx={{ position: 'absolute', bottom: 8, right: 8, textAlign: 'center' }}>
                  <Box position="relative" display="inline-flex">
                    <CircularProgress
                      variant="determinate"
                      value={parseFloat(percent)}
                      size={30}
                      thickness={4}
                      sx={{ color: '#1976d2', borderRadius: '50%' }}
                    />
                    <Box
                      top={0}
                      left={0}
                      bottom={0}
                      right={0}
                      position="absolute"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" sx={{ position: 'absolute', fontSize: 10 }}>
                        %{Math.round(percent)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            </Paper>
          );
        })}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddClick} sx={{ mt: 2 }}>
        Süzme Sayaç Ekle
      </Button>
      <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIndex !== null ? 'Sayaç Düzenle' : 'Yeni Sayaç Ekle'}</DialogTitle>
        <DialogContent>
          <ConsumptionInputs
            validIndexTypes={indexTypes.filter(it => it.indexType !== 'P' && it.indexType !== 'D')}
            values={formValues.consumptions || {}}
            onChange={(newValues) =>
              setFormValues((prev) => ({ ...prev, consumptions: newValues }))
            }
          />
          <TextField
            fullWidth
            label="Sayaç İsmi"
            margin="dense"
            value={formValues.name || ''}
            onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
          />
          <FormGroup sx={{ mt: 2 }}>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={formValues.lowPriority} onChange={(e) =>
                    setFormValues((prev) => ({ ...prev, lowPriority: e.target.checked }))
                  } />}
              label="Bu süzme sayaç ikinci öncelikli olsun"
            />

            <Typography variant="caption" color="textSecondary">
              Bazı tarifelerde kademeli fiyatlandırma uygulanır. Örneğin, günlük 8 kWh'e kadar olan tüketim ucuz, sonrası daha pahalı olabilir. Bu durumda ucuz tarifeyi önce 1. öncelikli sayaçlar (ana sayaç ile eşit olanlar) kullanır. Düşük öncelikli sayaçlar eğer kaldıysa ucuz tarifeden ücretlendirilir.
            </Typography>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>İptal</Button>
          <Button variant="contained" onClick={handleSave}>Kaydet</Button>
        </DialogActions>
      </Dialog>


      <Dialog open={detailDialogOpen} onClose={handleDetailClose}>
        <DialogTitle>{getMeterLabel(selectedMeter)}</DialogTitle>
        <DialogContent dividers>

          <TableContainer component={Paper} elevation={0}>
            <Table size="small" aria-label="süzme sayaç detay tablosu">
              <TableBody>
                {selectedMeter && (
                  <>
                    {/* Kovalar (Aktif, Dağıtım vs) */}
                    {Object.entries(
                      selectedMeter.bucketConsumptions.reduce((acc, bucket) => {
                        const name = bucket.bucketName?.replace('Kovası', '').trim();
                        const total = bucket.thresholdConsumptions?.reduce((sum, tc) => sum + tc.charge, 0) || 0;
                        acc[name] = (acc[name] || 0) + total;
                        return acc;
                      }, {})
                    ).map(([name, total]) => (
                      <TableRow key={name}>
                        <TableCell><strong>{name}</strong></TableCell>
                        <TableCell align="right">{formatNumber(total)} TL</TableCell>
                      </TableRow>
                    ))}

                    {/* Vergiler */}
                    {selectedMeter.taxesByTypeId && Object.entries(selectedMeter.taxesByTypeId).map(([taxType, amount]) => (
                      <TableRow key={taxType}>
                        <TableCell><strong>{taxType}</strong></TableCell>
                        <TableCell align="right">{formatNumber(amount)} TL</TableCell>
                      </TableRow>
                    ))}

                    {/* Toplam */}
                    <TableRow>
                      <TableCell><strong>Toplam</strong></TableCell>
                      <TableCell align="right"><strong>{formatNumber(selectedMeter.totalCharge)} TL</strong></TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>



        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose}>Kapat</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default SubMeterManager;
